import { useState, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react'

interface DateRangePickerProps {
  from: string
  to: string
  datesWithData: string[]
  onChange: (from: string, to: string) => void
}

const DAYS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di']

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

function toLocalDateStr(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatShort(dateStr: string): string {
  const [, month, day] = dateStr.split('-')
  const m = MONTHS[parseInt(month, 10) - 1].slice(0, 3)
  return `${parseInt(day, 10)} ${m}`
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstWeekday(year: number, month: number): number {
  // ISO weekday: Monday = 0
  const day = new Date(year, month, 1).getDay()
  return (day + 6) % 7
}

export default function DateRangePicker({ from, to, datesWithData, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [selecting, setSelecting] = useState<'from' | 'to'>('from')
  const [hoverDate, setHoverDate] = useState<string | null>(null)
  const [year, setYear] = useState(() => {
    return from ? parseInt(from.split('-')[0], 10) : new Date().getFullYear()
  })
  const [month, setMonth] = useState(() => {
    return from ? parseInt(from.split('-')[1], 10) - 1 : new Date().getMonth()
  })
  const ref = useRef<HTMLDivElement>(null)
  const dataSet = new Set(datesWithData)
  const todayStr = toLocalDateStr(new Date())

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false)
        setSelecting('from')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1) }
    else setMonth(m => m - 1)
  }

  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1) }
    else setMonth(m => m + 1)
  }

  function handleDayClick(dateStr: string) {
    if (selecting === 'from') {
      // Reset range start — keep to only if it's after the new from
      onChange(dateStr, dateStr > to ? dateStr : to)
      setSelecting('to')
    } else {
      if (dateStr < from) {
        onChange(dateStr, from)
      } else {
        onChange(from, dateStr)
      }
      setSelecting('from')
      setOpen(false)
    }
  }

  // Build calendar cells
  const totalDays = getDaysInMonth(year, month)
  const startOffset = getFirstWeekday(year, month)
  const cells: (string | null)[] = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= totalDays; d++) {
    const m = String(month + 1).padStart(2, '0')
    const day = String(d).padStart(2, '0')
    cells.push(`${year}-${m}-${day}`)
  }

  const effectiveTo = hoverDate && selecting === 'to' ? hoverDate : to

  return (
    <div className="relative" ref={ref}>
      {/* Trigger button */}
      <button
        onClick={() => { setOpen(o => !o); setSelecting('from') }}
        className="flex items-center gap-2 px-3 py-1.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg text-xs text-[#1B2559] hover:border-[#C7CAD9] transition-colors"
      >
        <Calendar className="w-3.5 h-3.5 text-[#8B8FA7]" strokeWidth={1.25} />
        <span>{formatShort(from)}</span>
        <span className="text-[#8B8FA7] mx-0.5">→</span>
        <span>{formatShort(to)}</span>
      </button>

      {/* Calendar dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-[#E2E4ED] rounded-xl shadow-xl p-3 w-[268px]">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={prevMonth}
              className="p-1 hover:bg-[#F4F5FA] rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />
            </button>
            <span className="text-sm font-medium text-[#1B2559]">
              {MONTHS[month]} {year}
            </span>
            <button
              onClick={nextMonth}
              className="p-1 hover:bg-[#F4F5FA] rounded-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d => (
              <div key={d} className="text-center text-[10px] font-medium text-[#8B8FA7] py-0.5">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-px">
            {cells.map((dateStr, i) => {
              if (!dateStr) return <div key={`empty-${i}`} />

              const hasData = dataSet.has(dateStr)
              const isFrom = dateStr === from
              const isTo = dateStr === to
              const isEndpoint = isFrom || isTo
              const inRange = dateStr > from && dateStr < effectiveTo && !isEndpoint
              const isToday = dateStr === todayStr

              return (
                <button
                  key={dateStr}
                  onClick={() => handleDayClick(dateStr)}
                  onMouseEnter={() => setHoverDate(dateStr)}
                  onMouseLeave={() => setHoverDate(null)}
                  className={[
                    'relative aspect-square flex items-center justify-center text-[11px] rounded-lg transition-colors',
                    isEndpoint
                      ? 'bg-orion-primary text-white font-semibold'
                      : inRange
                        ? 'bg-orion-primary-soft text-[#1B2559]'
                        : hasData
                          ? 'bg-emerald-50 text-[#1B2559] hover:bg-emerald-100'
                          : 'hover:bg-[#F4F5FA] text-[#555A73]',
                    isToday && !isEndpoint ? 'ring-1 ring-inset ring-[#C7CAD9]' : '',
                  ].join(' ')}
                >
                  {parseInt(dateStr.split('-')[2], 10)}
                </button>
              )
            })}
          </div>

          {/* Legend + hint */}
          <div className="mt-3 pt-2 border-t border-[#F4F5FA] flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200 inline-block" />
              <span className="text-[10px] text-[#8B8FA7]">Données disponibles</span>
            </div>
            <span className="text-[10px] text-[#8B8FA7]">
              {selecting === 'from' ? 'Début' : 'Fin'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
